import React, {FormEvent, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {observer} from "mobx-react-lite";
import intl from "react-intl-universal";
import {toJS} from "mobx";
import {getSnapshot} from "mobx-state-tree";
import {v4 as uuidv4} from 'uuid';

import {FileUploadsManager} from "../../../../stores/models/FileUploadsManager";
import {TaskReportFileMeta} from "../../../../stores/taskLists/TaskReportModel";
import {TaskDS} from "../../../../stores/taskLists/TaskDS";

import {AttachFiles} from "../../../components/AttachFiles/AttachFiles";
import {useAppState, useServiceRegistry, useUserState} from "@stores/AppStateContext";
import {InfoBlockPlain} from "../../../components/InfoBlockRow/InfoBlockPlain";
import {InputWithClear} from "../../../components/InputWithClear"
import {SideModal} from "../components/SideModal/SideModal";
import {SideModalContentBlock} from "../components/SideModal/SideModalContentBlock";
import {Button} from "../../../components/Button";
import {Textarea} from "../../../components/form/Textarea";
import {RelatedItemsSelect} from "../../../elements/context-menu/RelatedItemContextMenu/RelatedItemsSelect"
import {FieldWithCheckbox} from "../components/SideModal/FieldWithCheckbox";
import {ImageIcon} from "../../../components/icons/ImageIcon";
import {FormHint} from "../../../components/FormHint"
import {InfoIcon} from "../../../components/icons/InfoIcon";
import {uploadTaskImage} from "../../../../utils/file/uploadTaskImage";

import {TaskModel, TaskModelEnv, TaskModelType} from "../../../../stores/taskLists/TaskModel";
import {MODALS, TaskEditProps} from "../modal-names";
import {snackBarRef} from "../../../../stores/snackbarRef";

import "./TaskEdit.css"

const getTaskModelByTaskId = (taskId: string, taskDs: TaskDS, env: TaskModelEnv) => {
    const task = taskDs.getTaskById(taskId);
    return TaskModel.create(toJS(task), env);
}

export const TaskEdit = observer((
    {
        taskListUnitModel,
        docId,
        taskListModel,
        taskId,
        calendarTable
    }: TaskEditProps
) => {
    const inputRef =useRef<HTMLInputElement>(null);
    const appState = useAppState();
    const userState = appState.userState!;
    const serviceRegistry = useServiceRegistry();
    const storageService = serviceRegistry.getStorageService();
    const taskModelEnv = useMemo(() => calendarTable.getTaskModelEnv(), [calendarTable]);

    if (taskId && !calendarTable.taskDs) {
        throw new Error('taskDs not ready');
    }
    const [hasError, setError] = useState<string[]>([]);
    const taskModelRef = useRef<TaskModelType | undefined>(
        taskId
            ? getTaskModelByTaskId(taskId, calendarTable.taskDs!, taskModelEnv)
            : (
                TaskModel.create({
                    id: uuidv4(),
                    isNew: true,
                    hasChanges: true,
                    taskListUnitId: taskListUnitModel.id,
                    taskListId: taskListModel ? taskListModel.id : null,
                    name: '',
                    relatedPage: docId,
                }, taskModelEnv)
            )
    );
    const taskModel = taskModelRef.current!;

    const uploadFn = useCallback(async (file: File) => {
        const {path, thumbPath} = await uploadTaskImage(
            file, 
            taskListUnitModel.id, 
            taskModel.id, 
            storageService,
            appState.userState!.tenantId
        );
        return {
            path: path,
            thumbPath: thumbPath,
            uploadedAt: (new Date()).toISOString(),
            userId: userState?.currentUserId!,
        }
    }, [storageService, taskListUnitModel.id, taskModel.id, appState.userState?.tenantId, userState?.currentUserId]);
    const onError = useCallback(() => snackBarRef.current?.enqueueSnackbar({
        variant: 'error',
        message: intl.get('upload_image.error').d('Failed to upload image.'),
    }), []);

    const imagesFileUploadManager = useRef(new FileUploadsManager<TaskReportFileMeta>({
        uploadFn: uploadFn,
        onError: onError,
        initialMetas: taskModel?.images || [],
        storageService,
        fileProvider: userState.fileProvider,
        tenantId: userState.tenantId
    })).current;

    useEffect(() => {
        const id = requestAnimationFrame(() => {
            inputRef.current?.focus();
        });
        return () => cancelAnimationFrame(id);
    }, []);

    if (!taskModel) {
        return null;
    }

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        event.stopPropagation();
        await imagesFileUploadManager.waitAll();
        if (!taskModel.name.trim()) {
            setError(prev => [...prev, "EmptyName"]);
            return;
        }
        if (imagesFileUploadManager.entries) {
            taskModel.setImages(imagesFileUploadManager.json);
        }
        taskModel?.save().then(() => {
            if (taskListModel && taskModel.lastSavedObject.id) {
                taskListModel.addChildId(taskModel.lastSavedObject.id)
                taskListUnitModel.setTaskList(getSnapshot(taskListModel));
                taskListUnitModel.save();
            }
        });
        appState.setModal(null);
    }

    return (
        <SideModal
            header={(
                taskId
                    ? intl.get('TaskEdit.update-task').d('Regular Task')
                    : intl.get('TaskEdit.create-task').d('Create Regular Task')
            )}
            open={true}
        >
            <form onSubmit={handleSubmit}>
                <SideModalContentBlock
                    isRequired={true}
                    header={intl.get('TaskEdit.name.header').d('Task Name')}
                    mainContent={(
                        <>
                            <InputWithClear
                                ref={inputRef}
                                withBackground={true}
                                Icon={null}
                                placeholder={intl.get('TaskEdit.name.placeholder').d('Task Name: e.g. Clean entrance area')}
                                value={taskModel.name}
                                onChange={(val) => {
                                    taskModel.setName(val);
                                    if (hasError.includes("EmptyName") && val.trim()) {
                                        setError(prev => prev.filter(e => e !== "EmptyName"));
                                    }
                                }}
                            />
                            {hasError.includes("EmptyName") &&
                                <FormHint
                                    type={"error"}
                                    icon={<InfoIcon size={16}/>}
                                    text={intl.get('TaskEdit.name.error').d('This field is required. Please enter a name for this task.')}
                                    className={"SideModalContentBlock__error"}
                                />
                            }
                        </>
                    )}
                />
                <SideModalContentBlock
                    header={intl.get('common.description').d('Description')}
                    mainContent={
                        <>
                            <Textarea
                                value={taskModel.description}
                                onChange={taskModel.setDescription}
                                placeholder={intl.get('TaskEdit.description.placeholder').d('Optional description or notes for this Task.')}
                                fixedHeight={120}
                            />
                            <AttachFiles
                                fileUploadsManager={imagesFileUploadManager}
                                fileProvider={userState.fileProvider}
                                className={"TaskReportEdit__AttachFileButton"}
                            />
                        </>
                    }
                />
                <SideModalContentBlock
                    header={intl.get('common.related.page').d('Related Page')}
                    description={intl.get('create.task.related.page.description').d('Link a Page with instructions or tips to help complete the task.')}
                    mainContent={
                        <RelatedItemsSelect
                            itemId={taskModel.relatedPage}
                            setItemId={taskModel.setPage}
                            placeholder={intl.get('create.task.related.page.placeholder').d('Type to select a Page')}
                        />
                    }
                />
                <SideModalContentBlock
                    header={intl.get('common.required.confirmation').d('Required Confirmation')}
                    description={intl.get('create.task.required.confirmation.description').d('What should the employee provide to confirm the task is done?')}
                    mainContent={
                        <FieldWithCheckbox
                            Icon={ImageIcon}
                            onChange={taskModel.setIsImageRequired}
                            checked={taskModel.isImageRequired}
                            label={intl.get('create.task.required.confirmation.label').d('Upload image')}
                        />
                    }
                    infoText={intl.get('create.task.required.confirmation.info').d('If selected, the task can’t be completed without this confirmation.')}
                />
                <div className="TaskEdit__buttons">
                    <Button
                        text={(
                            taskId
                                ? intl.get('common.save').d('Save')
                                : intl.get('TaskEdit.create-btn').d("Create Task")
                        )}
                        style={"solid"}
                        type={"primary"}
                        htmlType={"submit"}
                    />
                    {
                        taskId && (
                            <Button
                                className={"SideModal__deleteButton"}
                                text={intl.get('task.delete.button').d("Delete Task")}
                                style={"outlined"}
                                type={"danger"}
                                onClick={() => {
                                    const task = calendarTable.taskDs?.getTaskById(taskId);
                                    if (!task) {
                                        return;
                                    }
                                    appState.setModal({
                                        name: MODALS.TASK_DELETE,
                                        params: {
                                            task: task,
                                            taskListModel,
                                            taskListUnitModel
                                        }
                                    });
                                }}
                            />
                        )
                    }
                </div>
                {
                    taskId && (
                        <InfoBlockPlain
                            className="TaskEdit__info"
                            createdBy={taskModel.createdBy}
                            createdAt={taskModel.createdAt}
                            updatedBy={taskModel.updatedBy}
                            updatedAt={taskModel.updatedAt}
                        />
                    )
                }
            </form>
        </SideModal>
    )
})
